<template>
  <div class="e-multilanguage">
    <e-combobox
      v-model="activeLangDisplayValue"
      ref="inputRef"
      :items="activeLang?.langCode === 'RU' ? addressSuggestions : []"
      :label="label"
      :rules="[...(rules || []), ...(commonRules || [])]"
      :error="hasInsideErrors"
      :error-messages="activeLang ? errorMessages(activeLang) : []"
      item-title="value"
      return-object
      no-filter
      :loading="isSearchLoading && activeLang?.langCode === 'RU'"
      :focused="isShownMenu"
      @update:search="searchAddresses"
      @update:model-value="onAddressSelect"
    >
      <template #append-inner>
        <div
          class="e-multilanguage-field__flag"
          :class="{ 'e-multilanguage-field__flag--error': hasInsideErrors }"
          @mousedown.stop
          @mouseup.stop
          @click.prevent.stop="menuUpdate(true)"
        >
          <v-img v-if="activeLang" :src="getLanguageIcon(activeLang.langCode)" width="16" />
        </div>
      </template>
    </e-combobox>

    <v-menu
      v-model="isShownMenu"
      activator="parent"
      :open-on-click="false"
      :open-on-focus="false"
      :close-on-content-click="false"
      :content-class="[
        'e-multilanguage__content',
        {
          'e-multilanguage__content--error-offset': inputRef && inputRef?.input?.isValid === false,
          'e-multilanguage__content--error': hasInsideErrors || (inputRef && inputRef?.input?.isValid === false),
        },
      ]"
      @update:model-value="onMenuUpdate"
    >
      <v-list-item v-for="(item, index) in languagesValues" class="no-hover" :key="index">
        <e-combobox
          v-if="item.langCode === 'RU'"
          v-model="item.addressObject"
          :items="addressSuggestions"
          :error-messages="errorMessages(item)"
          :autofocus="index === 0"
          item-title="value"
          return-object
          no-filter
          :loading="isSearchLoading"
          @update:search="searchAddresses"
          @update:model-value="(value) => onMenuAddressSelect(value, item)"
        >
          <template #append>
            <div
              :class="{ transparentBg: !isActiveItem(item.langCode) }"
              class="e-multilanguage-field__flag"
              @click="changeActiveLang(index)"
            >
              <v-img :src="getLanguageIcon(item.langCode)" width="16" />
            </div>
          </template>
        </e-combobox>

        <e-input v-else v-model="item.value" :error-messages="errorMessages(item)" :autofocus="index === 0">
          <template #append>
            <div
              :class="{ transparentBg: !isActiveItem(item.langCode) }"
              class="e-multilanguage-field__flag"
              @click="changeActiveLang(index)"
            >
              <v-img :src="getLanguageIcon(item.langCode)" width="16" />
            </div>
          </template>
        </e-input>
      </v-list-item>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { computed, nextTick, ref, toRef, watch } from 'vue';
import { VInput } from 'vuetify/components';
import type { AddressSuggestion } from '@/core/types/suggestions';
import type { MultilanguageValue } from '@/core/utils/languages';
import { ECombobox, EInput } from '@/core/components/ui';
import { useSuggestions } from '@/core/composables/suggestions';
import { getDefaultMultilanguageValues, getLanguageIcon } from '@/core/utils/languages';
import { deepEquals } from '@/core/utils/object';

interface MultilanguageAddressValue extends MultilanguageValue {
  addressObject?: AddressSuggestion | null;
}

const props = defineProps<{
  modelValue: MultilanguageValue[] | null;
  label?: string;
  rules?: VInput['rules'];
  commonRules?: VInput['rules'];
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: MultilanguageValue[]): void;
}>();

const { getDadataAddressSuggestions } = useSuggestions();

const inputRef = ref();
const isSearchLoading = ref(false);
const addressSuggestions = ref<AddressSuggestion[]>([]);
const languagesValues = ref<MultilanguageAddressValue[]>([]);

const hasLocaleValue = (langCode: MultilanguageValue['langCode']) =>
  languagesValues.value.find((l) => l.langCode === langCode);

const filteredLanguagesValues = toRef(() =>
  languagesValues.value
    .filter((l) => !!l.value)
    .map((item) => ({
      langCode: item.langCode,
      value: item.value || '',
    })),
);

const stringToAddressObject = (value: string): AddressSuggestion | null => {
  if (!value || typeof value !== 'string') return null;

  return {
    value: value,
    data: { city: '' },
    unrestricted_value: value,
  };
};

const addressObjectToString = (obj: AddressSuggestion | string | null): string => {
  if (!obj) return '';

  if (typeof obj === 'string') return obj;

  return obj.value || '';
};

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      props.modelValue.forEach((locale) => {
        if (!hasLocaleValue(locale.langCode)) {
          const newValue: MultilanguageAddressValue = {
            ...locale,
            value: locale.value || '',
            addressObject: locale.value ? stringToAddressObject(locale.value) : null,
          };
          languagesValues.value.push(newValue);
        } else {
          const lang = languagesValues.value.find((l) => l.langCode === locale.langCode);
          if (lang) {
            lang.value = locale.value || '';

            if (locale.langCode === 'RU') {
              lang.addressObject = locale.value ? stringToAddressObject(locale.value) : null;
            }
          }
        }
      });
    }

    const defaultLocaleValues = getDefaultMultilanguageValues();
    defaultLocaleValues.forEach((locale) => {
      if (!hasLocaleValue(locale.langCode)) {
        languagesValues.value.push({
          ...locale,
          value: '',
          addressObject: null,
        });
      }
    });
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  languagesValues,
  () => {
    const cleanValues = filteredLanguagesValues.value;
    if (!deepEquals(cleanValues, props.modelValue)) {
      emit('update:modelValue', cleanValues);
    }
  },
  {
    deep: true,
  },
);

const isShownMenu = ref(false);
const activeItemLangCode = ref('RU');

const isActiveItem = (langCode: MultilanguageValue['langCode']) => langCode === activeItemLangCode.value;

const activeLang = toRef(() => languagesValues.value.find((item) => isActiveItem(item.langCode)));

const activeLangDisplayValue = computed({
  get: () => {
    if (!activeLang.value) return null;

    if (activeLang.value.langCode === 'RU') {
      return activeLang.value.addressObject || stringToAddressObject(activeLang.value.value);
    }

    return activeLang.value.value || '';
  },
  set: (value: AddressSuggestion | string | null) => {
    if (!activeLang.value) return;

    const addressString = addressObjectToString(value);

    activeLang.value.value = addressString;

    if (activeLang.value.langCode === 'RU') {
      activeLang.value.addressObject = typeof value === 'object' && value !== null ? value : null;
    }
  },
});

const changeActiveLang = (index: number) => {
  if (!languagesValues.value[index]) {
    return;
  }

  activeItemLangCode.value = languagesValues.value[index].langCode;
  isShownMenu.value = false;

  void nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus();
    }
  });
};

const menuUpdate = (value: boolean) => {
  isShownMenu.value = value;
};

const findActiveLang = () => {
  if (activeLang.value?.value) {
    return;
  }

  const newActiveLang = languagesValues.value.find((l) => !!l.value);

  if (newActiveLang) {
    activeItemLangCode.value = newActiveLang.langCode;
  }
};

const onAddressSelect = (value: AddressSuggestion | string | null) => {
  if (activeLang.value) {
    const addressString = addressObjectToString(value);

    activeLang.value.value = addressString;

    if (activeLang.value.langCode === 'RU') {
      activeLang.value.addressObject = typeof value === 'object' && value !== null ? value : null;
    }
  }
};

const onMenuAddressSelect = (value: AddressSuggestion | string | null, item: MultilanguageAddressValue) => {
  const addressString = addressObjectToString(value);

  item.value = addressString;

  if (item.langCode === 'RU') {
    item.addressObject = typeof value === 'object' && value !== null ? value : null;
  }
};

const onMenuUpdate = (value: boolean) => {
  if (!value) {
    findActiveLang();
  }
};

const insideInputsRules = (item: MultilanguageValue) => {
  if (item.langCode === activeItemLangCode.value) {
    return [...(props.rules || []), ...(props.commonRules || [])];
  }

  return props.commonRules || [];
};

const errorMessages = (item: MultilanguageValue): string[] => {
  if (!item.value || item.value.trim() === '') {
    return [];
  }
  
  const rules = insideInputsRules(item) || [];
  const messages: string[] = [];

  rules.forEach((rule) => {
    const result = (rule as (v: string) => string | boolean)(item.value);
    if (result !== true && typeof result === 'string') {
      messages.push(result);
    }
  });

  return messages;
};

const hasInsideErrors = computed(() => {
  const r = props.commonRules || [];
  return languagesValues.value.some((item) => {
    const errors = r.map((rule) => (rule as (v: string) => string | boolean)(item.value));
    return errors.some((error) => error !== true);
  });
});

const searchAddresses = useDebounceFn(async (searchString: string) => {
  if (activeLang.value?.langCode !== 'RU') {
    addressSuggestions.value = [];
    return;
  }
  
  if (searchString.length > 255) {
    addressSuggestions.value = [];
    return;
  }

  isSearchLoading.value = true;

  const result = await getDadataAddressSuggestions(searchString);
  addressSuggestions.value = result;

  isSearchLoading.value = false;
}, 500);
</script>

<style scoped lang="scss">
.e-multilanguage {
  &-field {
    &__flag {
      z-index: 9;

      padding: 10px 8px;

      background-color: $primary-light-color;
      border-radius: 6px;

      cursor: pointer;

      &.transparentBg {
        background-color: transparent;
      }

      &--error {
        background-color: $alert-bg-color;
      }

      @include hover {
        background-color: $primary-tonal-color;
      }
    }
  }
}

:deep(.e-multilanguage__content) {
  padding: 16px 0 8px !important;

  &[style*='--v-overlay-anchor-origin: bottom left'] {
    padding: 8px 0 8px !important;

    transform: translateY(-42px);
  }

  &.isTextarea {
    &[style*='--v-overlay-anchor-origin: bottom left'] {
      transform: translateY(-124px);
    }
  }

  .v-list-item {
    &:first-child {
      padding-top: 0;
    }
  }
}

:deep(.e-multilanguage__content--error-offset) {
  &[style*='--v-overlay-anchor-origin: bottom left'] {
    transform: translateY(-58px);
  }
}

:deep(.e-multilanguage__content--error-offset.isTextarea) {
  &[style*='--v-overlay-anchor-origin: bottom left'] {
    transform: translateY(-138px);
  }
}
</style>
